import { ChevronRight, Eye, Printer } from 'react-feather';
import PageLayout from '../../../components/PageLayout';
import {
  formatCurrency,
  formatDate,
  getItemById,
  getQueryString,
  hasExpired,
} from '../../../utils/common.util';
import useSWR from 'swr';
import { useState } from 'react';
import ReportsModal from './ReportsModal';
import {
  DatePicker,
  DatePickerInput,
  Select,
  SelectItem,
} from 'carbon-components-react';
import PrintOutHead from '../../../components/PrintOutHead';
import { PrintView } from '../../../components/PrintView/print-view.component';
import CustomButton from '../../../components/custom-button/custom-btn.component';
import { useUrlState } from '../../../utils/useUrlState';
import ExportReportButton from '../../../components/ExportReportButton';
import Expandable from '../../../components/expandable/Expandable/Expandable';
import { RenderIf } from '../../../components/RenderIf';

const initialData = {
  dailyBillingFeeId: '',
  dateFrom: '',
  dateTo: '',
  academicYearId: '',
  termId: '',
  classId: '',
  genericGroupingId: '',
};

export default function DailySummaryReport() {
  const pageTitle = `Daily Fees Summary Report`;

  const [isOpen, setIsOpen] = useState('');
  const [query, setQuery] = useUrlState(initialData);

  const { data: academicYears } = useSWR(
    'academic-year/list/?skip=0&take=100&query=',
  );
  const { data: terms } = useSWR(
    query.academicYearId
      ? `academic-year/terms/?academicYearId=${query.academicYearId}`
      : null,
  );
  const { data: classes } = useSWR('class/list');

  const { data: d } = useSWR(`daily-billing/fees/list`);
  const feeTypes = d?.dailyBillingFee ?? [];
  const selectedFeetype = getItemById(feeTypes, query.dailyBillingFeeId);
  const isGeneric = selectedFeetype?.type === 'Generic';

  const mainUrl = `daily-billing/generate-daily-summary-report`;
  const { data, isLoading, error } = useSWR(
    query.dateFrom && query.dateTo
      ? `${mainUrl}?${getQueryString(query)}`
      : null,
  );

  // const entries = dailySummaryData?.entries ?? [];
  const dateGroups = data?.response?.dateGroups ?? [];
  const grandTotals = data?.response?.grandTotals ?? {};
  const feeType = getItemById(feeTypes, query.dailyBillingFeeId);

  function isDisabled(item) {
    return hasExpired(item?.invoice?.gracePeriodEndDate);
  }

  const grandTotalsSummary = [
    {
      label: 'Number of Students',
      value: grandTotals?.numberOfStudents,
    },
    {
      label: 'Collections (GH₵)',
      value: formatCurrency(grandTotals?.collections),
    },
    {
      label: 'Deposit Paid (GH₵)',
      value: formatCurrency(grandTotals?.depositPaid),
    },
    {
      label: 'Arrears Paid (GH₵)',
      value: formatCurrency(grandTotals?.arrearsPaid),
    },
    {
      label: 'Total (GH₵)',
      value: formatCurrency(grandTotals?.total),
    },
    { label: '', value: '' },
  ];

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Daily Fees', to: '/daily-fees' },
        { label: pageTitle, active: true },
      ]}
      title={
        <div className="d-flex align-items-center" style={{ gap: '30px' }}>
          <h1 className="h-20">{pageTitle}</h1>{' '}
          <button
            className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn "
            style={{ gap: '4px' }}
            onClick={() => setIsOpen('reports')}
          >
            <Eye size="14" strokeWidth="2px" />
            <span>VIEW OTHER REPORTS</span>
          </button>
        </div>
      }
      isLoading={isLoading}
    >
      {/* FILTERS  */}
      <p className="mt-20 text-12">
        Select at least a start and end dates to show report.
      </p>
      <div className={`mt-10 ${isGeneric ? 'card-row-4' : 'card-row-3'}`}>
        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="start-date-picker"
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                dateFrom: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ dateFrom: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="start-date"
            id="start-date"
            name="dateFrom"
            labelText="Start Date"
            value={query.dateFrom}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ dateFrom: e.target.value })}
          />
        </DatePicker>
        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="end-date-picker"
          // minDate={
          //   new Date(query.dateFrom ?? '').toISOString().split('T')[0]
          // }
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                dateTo: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ dateTo: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="end-date"
            id="end-date"
            name="dateTo"
            labelText="End Date"
            value={query.dateTo}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ dateTo: e.target.value })}
          />
        </DatePicker>

        <Select
          labelText="Academic Year"
          onChange={(e) =>
            setQuery({ academicYearId: e.target.value, termId: '' })
          }
          value={query.academicYearId}
        >
          <SelectItem value="" text="All" />
          {academicYears?.map((year) => (
            <SelectItem key={year.id} value={year.id} text={year.description} />
          ))}
        </Select>
        <Select
          labelText="Term"
          disabled={!query.academicYearId}
          onChange={(e) => setQuery({ termId: e.target.value })}
          value={query.termId}
        >
          <SelectItem value="" text="All" />
          {terms?.map((term) => (
            <SelectItem key={term.id} value={term.id} text={term.description} />
          ))}
        </Select>
        <Select
          labelText="Fee Type"
          value={query.dailyBillingFeeId}
          onChange={(e) =>
            setQuery({
              dailyBillingFeeId: e.target.value,
              classId: '',
              genericGroupingId: '',
            })
          }
        >
          <SelectItem selected value="" text="All" />
          {feeTypes?.map((item) => (
            <SelectItem
              key={item.id}
              text={
                item.name +
                (isDisabled(item) && item?.invoice?.status
                  ? ` - [${item.invoice.status}]`
                  : '')
              }
              value={item.id}
              disabled={isDisabled(item)}
            />
          ))}
        </Select>
        <Select
          labelText={isGeneric ? 'Group' : 'Class'}
          disabled={!query.dailyBillingFeeId}
          onChange={(e) =>
            setQuery(
              isGeneric
                ? { genericGroupingId: e.target.value, classId: '' }
                : { classId: e.target.value, genericGroupingId: '' },
            )
          }
          value={isGeneric ? query.genericGroupingId : query.classId}
        >
          <SelectItem value="" text="All" />
          {selectedFeetype?.genericAmounts?.map((item) => (
            <SelectItem key={item.id} text={item.name} value={item.id} />
          ))}
          {selectedFeetype?.classAmounts?.map((item) => (
            <SelectItem
              key={item.id}
              text={item.classDescription}
              value={item.classId}
            />
          ))}
        </Select>
        <RenderIf condition={isGeneric}>
          <Select
            labelText={'Class'}
            disabled={!query.dailyBillingFeeId}
            onChange={(e) => setQuery({ classId: e.target.value })}
            value={query.classId}
          >
            <SelectItem value="" text="All" />
            {classes?.map((term) => (
              <SelectItem
                key={term.id}
                value={term.id}
                text={term.description}
              />
            ))}
          </Select>
        </RenderIf>
      </div>

      <div className="mt-20">
        {error && <p className="text-14 mt-10 text-danger">{error.message}</p>}
        {dateGroups?.length > 0 && (
          <>
            <PrintOutHead
              title={pageTitle}
              headings={[
                {
                  label: 'Fee Type',
                  value: feeType?.name ?? 'All',
                },
                {
                  label: 'Academic Year',
                  value:
                    getItemById(academicYears, query.academicYearId)
                      ?.description ?? 'All',
                },
                {
                  label: 'Term',
                  value: getItemById(terms, query.termId)?.description ?? 'All',
                },
                {
                  label: 'Class',
                  value:
                    getItemById(classes, query.classId)?.description ?? 'All',
                },
                {
                  label: 'Date Range',
                  value: query.dateFrom
                    ? `${formatDate(query.dateFrom)} - ${formatDate(
                        query.dateTo,
                      )} `
                    : '-',
                },
              ]}
            />
            <PrintView>
              <div style={{ overflow: 'auto' }}>
                {dateGroups.map((group) => (
                  <GroupSection key={group.date} groupData={group} />
                ))}

                {/* Grand totals */}
                <div className="d-flex justify-between align-items-center py-12 px-16 bg-primary text-white rounded-4 mt-30">
                  <p className="font-bold">Grand Total</p>

                  {grandTotalsSummary?.map((total) => (
                    <div key={total.label} className="grid gap-10 text-center">
                      <span className="text-10">{total.label}</span>
                      <span className="font-bold text-18">{total.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </PrintView>
          </>
        )}
        {dateGroups?.length === 0 && (
          <p className="text-14 mt-30 text-grey">No data available.</p>
        )}

        {dateGroups?.length > 0 && (
          <div className="mt-30 d-flex gap-20 align-items-center flex-end">
            <ExportReportButton
              apiUrl={`${mainUrl}/export?${getQueryString(query)}`}
              fileName={`${pageTitle}.xlsx`}
            />
            <CustomButton classes="secondary" onClick={() => window.print()}>
              <Printer size="16" strokeWidth="2px" /> &nbsp; Print
            </CustomButton>
          </div>
        )}
      </div>

      <ReportsModal
        isOpen={isOpen === 'reports'}
        closeModal={() => setIsOpen('')}
      />
    </PageLayout>
  );
}

const GroupSection = ({ groupData }) => {
  const dateTotals = groupData.dateTotals;
  const [isExpanded, setIsExpanded] = useState(false);

  const totals = [
    { label: 'Number of Students', value: dateTotals.numberOfStudents },
    {
      label: 'Collections (GH₵)',
      value: formatCurrency(dateTotals.collections),
    },
    {
      label: 'Deposit Paid (GH₵)',
      value: formatCurrency(dateTotals.depositPaid),
    },
    {
      label: 'Arrears Paid (GH₵)',
      value: formatCurrency(dateTotals.arrearsPaid),
    },
    { label: 'Total (GH₵)', value: formatCurrency(dateTotals.total) },
  ];

  return (
    <div className="mt-30 bg-white p-16 rounded">
      <div className="d-flex justify-between align-items-start ">
        <p className="font-bold">{formatDate(groupData.date)}</p>
        {totals?.map((total, index) => (
          <div
            key={index}
            className="grid gap-10 text-center no-print"
            style={{ opacity: isExpanded ? 0 : 1, transition: 'opacity 0.3s' }}
          >
            <span className="text-10 text-grey">{total.label}</span>
            <span className="font-bold text-18">{total.value}</span>
          </div>
        ))}

        <button
          className="clean-btn"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          title={isExpanded ? 'Collapse' : 'Expand'}
        >
          <ChevronRight
            size={12}
            strokeWidth={5}
            style={{
              rotate: isExpanded ? '90deg' : '0deg',
              transition: 'rotate 0.3s',
            }}
          />
        </button>
      </div>
      <Expandable isExpanded={isExpanded} className="mt-10">
        <table className="bx--data-table table-white">
          <thead>
            <tr>
              <th>Fee Type</th>
              <th>Class/Group</th>
              <th>Number of Students</th>
              <th>Collections (GH₵)</th>
              <th>Deposit Paid (GH₵)</th>
              <th>Arrears Paid (GH₵)</th>
              <th>Total (GH₵)</th>
            </tr>
          </thead>
          <tbody>
            {groupData.entries.map((entry) => {
              return (
                <tr key={entry.id}>
                  <td>{entry.feeType}</td>
                  <td>{entry.classOrGrouping}</td>
                  <td>{entry.numberOfStudents}</td>
                  <td>{entry.collections}</td>
                  <td>{entry.depositPaid}</td>
                  <td>{entry.arrearsPaid}</td>
                  <td>{entry.total}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="font-bold">
              <td colSpan="2">Total</td>
              <td>{dateTotals.numberOfStudents}</td>
              <td>{formatCurrency(dateTotals.collections)}</td>
              <td>{formatCurrency(dateTotals.depositPaid)}</td>
              <td>{formatCurrency(dateTotals.arrearsPaid)}</td>
              <td>{formatCurrency(dateTotals.total)}</td>
            </tr>
          </tfoot>
        </table>
      </Expandable>
    </div>
  );
};
